<template>
  <div class="base-input" :class="[wrapperClass, {'has-error': errorMessage, 'disabled': disabled, 'has-value': hasValue, 'has-left-slot': hasLeftSlot, 'has-right-slot': hasRightSlot }]">
    <div class="base-input-field-wrapper grid grid-cols-1 grid-rows-1 items-center justify-start overflow-hidden relative z-20 border rounded bg-white text-text h-3em">
      <div v-if="hasLeftSlot" class="z-10 col-start-1 row-start-1 self-stretch w-3em items-center justify-center flex justify-self-start">
        <slot name="left" />
      </div>
      <label
        v-if="label"
        :for="id"
        :class="{ 'visually-hidden': hideLabel }"
      >
        <span v-text="labelText" />
      </label>
      <input
        :id="id"
        :name="name"
        :type="type"
        :placeholder="placeholderText"
        :required="required"
        :value="modelValue"
        :disabled="disabled"
        v-bind="$attrs"
        :class="{ 'pt-0': hideLabel }"
        @input="$emit('update:modelValue', $event.target.value)"
      >
      <div v-if="hasRightSlot" class="z-10 col-start-1 row-start-1 self-stretch w-3em items-center justify-center flex justify-self-end">
        <slot name="right" />
      </div>
    </div>
    <div
      v-if="hasValidator"
      :title="errorMessage"
      class="input-error-msg"
    >
      <span v-text="errorMessage" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseInput',
  inheritAttrs: false,
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    hideLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    wrapperClass: {
      type: [Object, Array, String],
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    validator: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  emits: ['update:modelValue'],
  computed: {
    hasValue() { return this.modelValue !== '' && this.modelValue !== undefined },
    hasValidator() { return false }, // @TODO: Fix Object(...) is not a function | !!Object.keys(this.validator).length
    hasLeftSlot() { return !!this.$slots.left },
    hasRightSlot() { return !!this.$slots.right },
    errorMessage() { return this.hasValidator && this.validator.$errors?.length ? this.validator.$errors[0].$message : '' },
    labelText() { return this.label + (this.required ? ' *' : '') },
    placeholderText() { return this.hideLabel ? this.placeholder : '' },
    id() { return this.$attrs.id || this.name },
  },
}
</script>

<style lang="scss">
.base-input {
  $gap: 1em;
  $spaceForSlot: 3.5em;
  @apply relative;

  .base-input-field-wrapper {
    > * {
      @apply col-start-1 row-start-1;
    }
    &:focus-within {
      @apply shadow-md border-rivulet;
    }
  }

  label {
    @apply text-silver antialiased block select-none whitespace-nowrap leading-none font-display tracking-wide z-10 pointer-events-none overflow-hidden;
    padding: 0 $gap;
    transition: all 150ms;
    > span {
      transition: all 150ms;
      @apply leading-loose;
    }
  }
  input {
    @apply w-full h-full px-0 leading-snug focus:outline-none text-text bg-transparent relative;
    padding: 10px $gap 0;
    &::placeholder {
      @apply font-display;
    }
  }

  .field-icon {
    @apply opacity-60;
  }
  &:focus-within .field-icon {
    @apply opacity-100;
  }

  &.has-left-slot label,
  &.has-left-slot input,
  &.has-left-slot .input-error-msg {
    padding-left: $spaceForSlot;
  }

  &.has-right-slot label,
  &.has-right-slot input {
    padding-right: $spaceForSlot;
  }

  &.disabled {
    @apply bg-whitesmoke;
    filter: saturate(0) brightness(0.98);
    > .base-input-field-wrapper {
      @apply opacity-75;
    }
  }

  &.has-value, &:focus-within {
    label {
      @apply text-rivulet;
      transform: translateY(-0.7em);
      > span {
        font-size: clamp(10px, 0.7em, 12px);
      }
    }
  }

  &.has-error:not(:focus-within) {
    .base-input-field-wrapper {
      border-bottom-color: theme('colors.error');
    }
    .input-error-msg {
      @apply opacity-100 translate-x-0;
    }
  }
  .input-error-msg {
    @apply pointer-events-none opacity-0 transition transition-transform transition-opacity transform duration-200 -translate-x-2 origin-top grid place-content-end absolute bg-white right-px bottom-px z-30 whitespace-nowrap overflow-ellipsis overflow-hidden text-right text-amaranth tracking-wide;
    padding: 0 $gap;
    > span {
      @apply text-10;
    }
  }
}
</style>
